import { passwordChangeSchema } from "@erp/shared";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { KeyRound, ShieldCheck, ShieldOff } from "lucide-react";
import QRCode from "qrcode";
import { useEffect, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Field } from "~/components/ui/field";
import { errorAt, schemaErrors } from "~/lib/forms";
import { Input } from "~/components/ui/input";
import { Panel, Segmented, Stat } from "~/components/ui/page";
import { Sheet } from "~/components/ui/sheet";
import { Skeleton } from "~/components/ui/states";
import { SettingsSection } from "~/features/settings/section";
import { authClient } from "~/lib/auth-client";
import { useLocale, useT } from "~/lib/i18n";
import { meQuery, useSession } from "~/lib/session";
import { useTheme, type ThemePreference } from "~/lib/theme";

export const Route = createFileRoute("/_app/settings/")({ component: AccountSettings });

function AccountSettings() {
  const t = useT();
  const user = useSession();
  const [theme, setTheme] = useTheme();
  const [locale, setLocale] = useLocale();

  return (
    <div className="flex flex-col gap-5">
      <Panel>
        <SettingsSection title={t("settings.profile")}>
          <dl className="grid gap-4 sm:grid-cols-3">
            <Stat label={t("common.fields.name")} value={user.name} />
            <Stat label={t("common.fields.email")} value={user.email} />
            <Stat label={t("settings.role")} value={t(`auth.roles.${user.role}`)} />
          </dl>
        </SettingsSection>
        <PasswordSection />
        <TwoFactorSection enabled={user.twoFactorEnabled} />
        <SettingsSection title={t("settings.preferences")}>
          <div className="flex flex-col gap-4">
            <Field label={t("common.theme.label")}>
              <div>
                <Segmented<ThemePreference>
                  label={t("common.theme.label")}
                  value={theme}
                  onChange={setTheme}
                  options={(["light", "dark", "system"] as const).map((v) => ({ value: v, label: t(`common.theme.${v}`) }))}
                />
              </div>
            </Field>
            <Field label={t("common.language.label")}>
              <div>
                <Segmented
                  label={t("common.language.label")}
                  value={locale}
                  onChange={setLocale}
                  options={(["fr", "en"] as const).map((v) => ({ value: v, label: t(`common.language.${v}`) }))}
                />
              </div>
            </Field>
          </div>
        </SettingsSection>
      </Panel>
    </div>
  );
}

const emptyPasswords = { currentPassword: "", newPassword: "", confirmPassword: "", revokeOtherSessions: true };

/** Change one's own password; other devices are signed out by default. */
function PasswordSection() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(emptyPasswords);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const errors = submitted ? schemaErrors(passwordChangeSchema, values) : {};
  const set = (key: "currentPassword" | "newPassword" | "confirmPassword") => (value: string) =>
    setValues((v) => ({ ...v, [key]: value }));

  const close = () => {
    setOpen(false);
    setValues(emptyPasswords);
    setSubmitted(false);
    setError(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setError(null);
    if (Object.keys(schemaErrors(passwordChangeSchema, values)).length) return;
    setPending(true);
    const { error: failure } = await authClient.changePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
      revokeOtherSessions: values.revokeOtherSessions,
    });
    setPending(false);
    if (failure)
      return setError(
        t(
          failure.status === 429
            ? "errors.rateLimited"
            : failure.status === 400 || failure.status === 401
              ? "settings.password.wrongCurrent"
              : "errors.generic",
        ),
      );
    toast.success(t("settings.password.changed"));
    close();
  };

  return (
    <SettingsSection title={t("settings.password.title")} description={t("settings.password.description")}>
      <Button onClick={() => setOpen(true)}>
        <KeyRound />
        {t("settings.password.change")}
      </Button>
      <Sheet
        open={open}
        onOpenChange={(next) => (next ? setOpen(true) : close())}
        title={t("settings.password.change")}
        description={t("settings.password.rules")}
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              {t("common.actions.cancel")}
            </Button>
            <Button variant="primary" type="submit" form="password-form" loading={pending}>
              {t("common.actions.save")}
            </Button>
          </>
        }
      >
        <form id="password-form" onSubmit={submit} noValidate className="flex flex-col gap-4">
          <Field label={t("settings.password.current")} error={errorAt(t, errors, "currentPassword") ?? error ?? undefined}>
            <Input
              type="password"
              autoComplete="current-password"
              value={values.currentPassword}
              onChange={(e) => set("currentPassword")(e.target.value)}
            />
          </Field>
          <Field label={t("settings.password.new")} error={errorAt(t, errors, "newPassword")}>
            <Input
              type="password"
              autoComplete="new-password"
              value={values.newPassword}
              onChange={(e) => set("newPassword")(e.target.value)}
            />
          </Field>
          <Field label={t("settings.password.confirm")} error={errorAt(t, errors, "confirmPassword")}>
            <Input
              type="password"
              autoComplete="new-password"
              value={values.confirmPassword}
              onChange={(e) => set("confirmPassword")(e.target.value)}
            />
          </Field>
          <label className="flex min-h-11 cursor-pointer items-start gap-2.5 text-sm md:min-h-0">
            <input
              type="checkbox"
              checked={values.revokeOtherSessions}
              onChange={(e) => setValues((v) => ({ ...v, revokeOtherSessions: e.target.checked }))}
              className="mt-0.5 size-4 accent-[var(--brand-gold)]"
            />
            {t("settings.password.revokeOthers")}
          </label>
        </form>
      </Sheet>
    </SettingsSection>
  );
}

type Step =
  | { kind: "idle" }
  | { kind: "password"; mode: "enable" | "disable" }
  | { kind: "scan"; uri: string; backupCodes: string[] }
  | { kind: "backup"; codes: string[] };

function TwoFactorSection({ enabled }: { enabled: boolean }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>({ kind: "idle" });
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    if (step.kind !== "scan") return;
    void QRCode.toDataURL(step.uri, { margin: 1, width: 220 }).then(setQr);
  }, [step]);

  const close = () => {
    setStep({ kind: "idle" });
    setPassword("");
    setCode("");
    setError(null);
    setQr(null);
  };

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();
    if (step.kind !== "password") return;
    setPending(true);
    setError(null);
    if (step.mode === "enable") {
      const { data, error: failure } = await authClient.twoFactor.enable({ password });
      setPending(false);
      if (failure || !data || data.method !== "totp") return setError(t("errors.codes.auth.invalid_credentials"));
      setStep({ kind: "scan", uri: data.totpURI, backupCodes: data.backupCodes });
    } else {
      const { error: failure } = await authClient.twoFactor.disable({ password });
      setPending(false);
      if (failure) return setError(t("errors.codes.auth.invalid_credentials"));
      await queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
      toast.success(t("settings.twoFactor.disabledToast"));
      close();
    }
  };

  const submitCode = async (event: FormEvent) => {
    event.preventDefault();
    if (step.kind !== "scan") return;
    setPending(true);
    setError(null);
    const { error: failure } = await authClient.twoFactor.verifyTotp({ code });
    setPending(false);
    if (failure) return setError(t("errors.codes.auth.two_factor_invalid"));
    await queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
    toast.success(t("settings.twoFactor.enabledToast"));
    setStep({ kind: "backup", codes: step.backupCodes });
  };

  const secret = step.kind === "scan" ? new URL(step.uri).searchParams.get("secret") : null;

  return (
    <SettingsSection title={t("settings.twoFactor.title")} description={t("settings.twoFactor.description")}>
      <div className="flex flex-wrap items-center gap-3">
        {enabled ? (
          <Badge tone="success">
            <ShieldCheck className="size-3.5" aria-hidden />
            {t("settings.twoFactor.enabled")}
          </Badge>
        ) : (
          <Badge tone="warning">
            <ShieldOff className="size-3.5" aria-hidden />
            {t("settings.twoFactor.disabled")}
          </Badge>
        )}
        <Button
          variant={enabled ? "secondary" : "primary"}
          size="sm"
          onClick={() => setStep({ kind: "password", mode: enabled ? "disable" : "enable" })}
        >
          {enabled ? t("settings.twoFactor.disable") : t("settings.twoFactor.enable")}
        </Button>
      </div>

      <Sheet
        open={step.kind !== "idle"}
        onOpenChange={(open) => (open ? undefined : close())}
        title={t("settings.twoFactor.title")}
      >
        {step.kind === "password" ? (
          <form onSubmit={submitPassword} className="flex flex-col gap-4" noValidate>
            <Field label={t("settings.twoFactor.password")} error={error ?? undefined}>
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <Button type="submit" variant="primary" loading={pending} disabled={!password} className="self-start">
              {t("settings.twoFactor.confirm")}
            </Button>
          </form>
        ) : null}
        {step.kind === "scan" ? (
          <form onSubmit={submitCode} className="flex flex-col gap-4" noValidate>
            <p className="text-sm text-fg-2">{t("settings.twoFactor.scan")}</p>
            <div className="self-center rounded-panel bg-white p-3">
              {qr ? <img src={qr} alt="" width={220} height={220} /> : <Skeleton className="size-[220px]" />}
            </div>
            {secret ? (
              <p className="text-xs text-fg-3">
                {t("settings.twoFactor.manualKey")}{" "}
                <code className="rounded bg-sunken px-1.5 py-0.5 text-fg select-all">{secret}</code>
              </p>
            ) : null}
            <Field label={t("auth.twoFactorCode")} error={error ?? undefined}>
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="text-center text-lg tracking-[0.4em] tabular"
              />
            </Field>
            <Button type="submit" variant="primary" loading={pending} disabled={code.length !== 6} className="self-start">
              {t("auth.verify")}
            </Button>
          </form>
        ) : null}
        {step.kind === "backup" ? (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-fg-2">{t("settings.twoFactor.backupHint")}</p>
            <ul className="grid grid-cols-2 gap-2 rounded-panel bg-sunken p-4 font-mono text-sm tabular">
              {step.codes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <Button variant="primary" onClick={close} className="self-start">
              {t("settings.twoFactor.done")}
            </Button>
          </div>
        ) : null}
      </Sheet>
    </SettingsSection>
  );
}
